import { useCallback, useRef, useState } from 'react';
import { CustomModal } from '../../CustomModal';
import CashierReportTable, {
    CashierReportTableHandle,
} from './CashierReportTable';
import CashierReportHeader from './CashierReportHeader';
import { useTranslation } from 'react-i18next';
import { utils, writeFile } from 'xlsx';

export interface CashierData {
    id: number;
    cashier: { [key: number]: number };
    cashierAdjusted: { [key: number]: number };
    money: number;
    user: {
        id: number;
        name: string;
    };
    machine: {
        id: number;
        name: string;
    };
    location: {
        id: number;
        name: string;
    };
    ts: Date;
}

export type CashierReportResponse = CashierData[];

export interface CashierReportModalProps {
    data: CashierReportResponse;
    onClose: () => void;
}

export default function CashierReportModal({
    data,
    onClose,
}: CashierReportModalProps) {
    const [open, setOpen] = useState(true);
    const tableRef = useRef<CashierReportTableHandle>(null);
    const { t } = useTranslation();

    const onCloseHandler = useCallback(() => {
        setOpen(false);
        setTimeout(onClose, 500);
    }, [onClose]);

    const getFileRows = useCallback(() => {
        const banknotes = tableRef.current?.getAvailableCoins() ?? [];
        const rows = tableRef.current?.getCurrentTableData() ?? [];
        return [
            [
                t('date'),
                t('machine'),
                t('location'),
                t('user'),
                ...banknotes.map(String),
                t('total'),
                t('value'),
                t('status_title'),
            ],
            ...rows.map((el) => [
                new Date(el.ts).toLocaleString(),
                el.machine.name,
                el.location.name,
                el.user.name,
                ...banknotes.map((bn) => el.cashierAdjusted[bn] ?? 0),
                Object.keys(el.cashierAdjusted)
                    .map((bn) => Number(bn) * (el.cashierAdjusted[bn] ?? 0))
                    .reduce((prev, crt) => prev + crt, 0),
                el.money,
                Object.keys(el.cashierAdjusted)
                    .map((bn) => Number(bn) * (el.cashierAdjusted[bn] ?? 0))
                    .reduce((prev, crt) => prev + crt, 0) == el.money
                    ? t('ok')
                    : t('invalid'),
            ]),
        ];
    }, [tableRef, t]);

    const getFileName = useCallback(() => {
        const now = new Date();
        const year = now.getFullYear().toString();
        const month = (now.getMonth() + 1).toString().padStart(2, '0');
        const day = now.getDate().toString().padStart(2, '0');
        return `cashier_${year}_${month}_${day}`;
    }, []);

    const exportToExcel = useCallback(() => {
        const ws = utils.aoa_to_sheet(getFileRows());
        const wb = utils.book_new();

        utils.book_append_sheet(wb, ws, 'Sheet1');
        writeFile(wb, `${getFileName()}.xlsx`);
    }, [getFileRows, getFileName]);

    const exportToCSV = useCallback(() => {
        const ws = utils.aoa_to_sheet(getFileRows());
        const wb = utils.book_new();

        utils.book_append_sheet(wb, ws, 'Sheet1');
        const csv = utils.sheet_to_csv(ws);
        const blob = new Blob([csv], { type: 'text/plain' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${getFileName()}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
    }, [getFileRows, getFileName]);

    return (
        <CustomModal
            open={open}
            onClose={onCloseHandler}
            title={t('cashier')}
            header={
                <CashierReportHeader
                    onExportExcel={exportToExcel}
                    onExportCSV={exportToCSV}
                />
            }
            dialogStyle={{
                width: '100vw',
                maxWidth: 'none',
                paddingLeft: 10,
                paddingRight: 10,
            }}
            bodyStyle={{ height: '85vh' }}
        >
            <CashierReportTable ref={tableRef} data={data} />
        </CustomModal>
    );
}
